import React, { useState, useEffect, useRef, useCallback } from 'react';
import API from 'shared/api';
import {
  FiDollarSign,
  FiFileText,
  FiAlertTriangle,
  FiTrendingUp,
  FiPackage,
  FiUsers,
  FiDownload,
  FiBarChart2,
  FiLayers,
  FiSearch,
  FiX,
  FiShoppingCart,
  FiList,
  FiArrowUp,
  FiArrowDown,
  FiCalendar,
} from 'react-icons/fi';
import { COL } from 'shared/darkColors';
import {
  money,
  intFmt,
  Card,
  Empty,
  SkeletonBlock,
  LineChart,
  BarsH,
} from 'shared/components/Dashboard/charts';

const PRESETS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: '1y', label: '1 año' },
  { id: 'all', label: 'Todo' },
  { id: 'custom', label: 'Personalizado' },
];

function rangoDePreset(id) {
  const hoy = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const shift = (dias) => {
    const d = new Date(hoy);
    d.setDate(d.getDate() - dias);
    return iso(d);
  };
  if (id === '7d') return [shift(7), iso(hoy)];
  if (id === '30d') return [shift(30), iso(hoy)];
  if (id === '90d') return [shift(90), iso(hoy)];
  if (id === '1y') return [shift(365), iso(hoy)];
  if (id === 'all') return ['', ''];
  return [shift(30), iso(hoy)];
}

// Estado persistido en localStorage (sobrevive a cerrar/recargar).
function usePersistedState(key, initial) {
  const [value, setValue] = useState(() => {
    try {
      const s = localStorage.getItem(key);
      return s ? JSON.parse(s) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* noop */
    }
  }, [key, value]);
  return [value, setValue];
}

const S = {
  page: { padding: '20px', maxWidth: '1400px', margin: '0 auto' },
  presetBtn: {
    background: COL.white,
    border: `1px solid ${COL.border}`,
    color: COL.text,
    fontSize: '12px',
    fontWeight: 600,
    padding: '6px 10px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  presetActive: { background: COL.pri, color: '#fff', borderColor: COL.pri },
  input: {
    background: COL.white,
    border: `1px solid ${COL.border}`,
    color: COL.text,
    fontSize: '12px',
    padding: '6px 8px',
    borderRadius: '8px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: {
    textAlign: 'left',
    padding: '8px',
    color: COL.muted,
    fontSize: '10px',
    textTransform: 'uppercase',
    fontWeight: 700,
    borderBottom: `1px solid ${COL.border}`,
  },
  td: { padding: '8px', borderBottom: `1px solid ${COL.border}` },
  rowHover: { cursor: 'pointer' },
};

function KPI({ label, value, sub, color, big, icon: Icon }) {
  return (
    <div
      style={{
        background: big
          ? `linear-gradient(135deg, ${COL.green}, #0f9d58)`
          : COL.white,
        borderRadius: '12px',
        padding: '14px',
        border: big ? 'none' : `1px solid ${COL.border}`,
        borderLeft: big ? 'none' : `4px solid ${color}`,
        boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: big ? 'rgba(255,255,255,0.85)' : COL.muted }}>
          {label}
        </span>
        {Icon && <Icon size={15} color={big ? '#fff' : color} />}
      </div>
      <div style={{ fontSize: big ? '32px' : '22px', fontWeight: 800, color: big ? '#fff' : color, lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: '11px', color: big ? 'rgba(255,255,255,0.85)' : COL.muted, marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

const COLORS_ESTADO = {
  COBRADA: COL.green,
  'POR COBRAR': COL.red,
  ABONADA: COL.amber,
};

// Traduccion + agrupado de los estados crudos del ERP. ADVIN usa dos variantes de
// devolucion ('DEVUELTA T' y el typo 'DEVUESLA T'); ambas se agrupan en 'Devuelta'.
const ESTADO_LABEL = {
  COBRADA: 'Cobrada',
  'POR COBRAR': 'Por cobrar',
  ABONADA: 'Abonada',
  FACTURADA: 'Facturada',
};
function estadoLabel(estado) {
  if (!estado) return 'Sin estado';
  if (estado.startsWith('DEVU')) return 'Devuelta';
  return ESTADO_LABEL[estado] || estado;
}
function estadoColor(estado) {
  if (!estado) return COL.muted;
  if (estado.startsWith('DEVU')) return COL.red;
  return COLORS_ESTADO[estado] || COL.muted;
}

// ── Modal genérico de detalle ──
function Modal({ title, subtitle, onClose, children }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px', overflowY: 'auto' }}
      onClick={onClose}
    >
      <div
        style={{ background: COL.white, borderRadius: '14px', maxWidth: '960px', width: '100%', boxShadow: '0 12px 40px rgba(16,24,40,0.25)', border: `1px solid ${COL.border}`, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px 12px', borderBottom: `1px solid ${COL.border}` }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: 800, color: COL.pri }}>{title}</div>
            {subtitle && <div style={{ fontSize: '12px', color: COL.muted, marginTop: '2px' }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.muted, padding: '4px', flexShrink: 0 }}>
            <FiX size={20} />
          </button>
        </div>
        <div style={{ padding: '20px', overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

function Campo({ label, value }) {
  return (
    <div style={{ minWidth: '140px' }}>
      <div style={{ fontSize: '10px', color: COL.muted, textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: '13px', color: COL.pri, marginTop: '2px' }}>{value || '—'}</div>
    </div>
  );
}

// ── Tabla genérica ordenable ──
// columns: [{key, label, sortable, render, align}]. rows ya ordenados (cliente o servidor).
function DataTable({ columns, rows, sortCol, sortDir, onSort, onRowClick, rowKey, emptyText, footer }) {
  return (
    <table style={S.table}>
      <thead>
        <tr style={{ background: COL.bg, position: 'sticky', top: 0, zIndex: 1 }}>
          {columns.map((c) => (
            <th
              key={c.key}
              style={{ ...S.th, ...(c.align ? { textAlign: c.align } : {}), cursor: c.sortable === false ? 'default' : 'pointer', userSelect: 'none' }}
              onClick={() => c.sortable !== false && onSort(c.key)}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {c.label}
                {sortCol === c.key && c.sortable !== false && (sortDir === 'asc' ? <FiArrowUp size={10} /> : <FiArrowDown size={10} />)}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={rowKey(r)}
            onClick={() => onRowClick && onRowClick(r)}
            style={onRowClick ? S.rowHover : undefined}
            onMouseEnter={onRowClick ? (e) => { e.currentTarget.style.background = COL.bg; } : undefined}
            onMouseLeave={onRowClick ? (e) => { e.currentTarget.style.background = ''; } : undefined}
          >
            {columns.map((c) => (
              <td key={c.key} style={{ ...S.td, ...(c.align ? { textAlign: c.align } : {}) }}>{c.render(r)}</td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && (
          <tr><td style={{ ...S.td, textAlign: 'center', color: COL.muted }} colSpan={columns.length}>{emptyText || 'Sin resultados'}</td></tr>
        )}
      </tbody>
      {footer && <tfoot>{footer}</tfoot>}
    </table>
  );
}

function sortRows(rows, col, dir) {
  const mul = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let av = a[col];
    let bv = b[col];
    if (typeof av === 'number' && typeof bv === 'number') {
      /* ok */
    } else if (typeof av === 'string' && typeof bv === 'string') {
      av = av.toLowerCase();
      bv = bv.toLowerCase();
    } else {
      av = String(av == null ? '' : av).toLowerCase();
      bv = String(bv == null ? '' : bv).toLowerCase();
    }
    if (av < bv) return -1 * mul;
    if (av > bv) return 1 * mul;
    return 0;
  });
}

// ── Buscador ──
function SearchBox({ value, onChange, placeholder, countLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', flex: 1, maxWidth: '380px', minWidth: '220px' }}>
        <FiSearch size={14} style={{ position: 'absolute', left: '10px', top: '9px', color: COL.muted }} />
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ ...S.input, width: '100%', paddingLeft: '30px' }} />
      </div>
      <span style={{ fontSize: '12px', color: COL.muted }}>{countLabel}</span>
    </div>
  );
}

// ── Vista Resumen ──
function ResumenView({ fechaDesde, fechaHasta, onVerProductos, onVerClientes, onVerVentas }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setCargando(true);
    setError(false);
    const p = new URLSearchParams();
    if (fechaDesde) p.set('fecha_desde', fechaDesde);
    if (fechaHasta) p.set('fecha_hasta', fechaHasta);
    API.get(`/ventas/erp/resumen?${p.toString()}`)
      .then((r) => { if (active) { if (r.data.status === 'success') setData(r.data.data); else setError(true); } })
      .catch(() => active && setError(true))
      .finally(() => active && setCargando(false));
    return () => { active = false; };
  }, [fechaDesde, fechaHasta]);

  const exportarCSV = () => {
    if (!data) return;
    const d = data;
    const rows = [];
    rows.push(['Métrica', 'Valor']);
    rows.push(['Facturas', d.totales.facturas]);
    rows.push(['Ventas totales USD', d.totales.total_usd.toFixed(2)]);
    rows.push(['Ticket promedio USD', d.totales.ticket_promedio_usd.toFixed(2)]);
    rows.push(['Cartera por cobrar USD', d.cartera.saldo_usd.toFixed(2)]);
    rows.push([]);
    rows.push(['Top productos', '', '', '', '']);
    rows.push(['Producto', 'Código', 'Vendido', 'Ingresos USD', 'Margen USD', 'Margen %']);
    (d.top_productos || []).forEach((p) => rows.push([p.nombre, p.codigo, p.vendido, p.ingresos_usd.toFixed(2), p.margen_usd.toFixed(2), p.ingresos_usd > 0 ? `${((p.margen_usd / p.ingresos_usd) * 100).toFixed(0)}%` : '—']));
    rows.push([]);
    rows.push(['Top clientes', '', '']);
    rows.push(['Cliente', 'RIF', 'Gasto USD']);
    (d.top_clientes || []).forEach((c) => rows.push([c.nombre, c.rif || '', c.ingresos_usd.toFixed(2)]));
    const csv = rows.map((r) => r.map((c) => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventas_erp_${fechaDesde || 'todo'}_a_${fechaHasta || 'todo'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (cargando && !data) {
    return (
      <div>
        <style>{`
          @keyframes dashShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
          .dash-skel { background: linear-gradient(90deg,#eef0f4 25%,#334155 37%,#eef0f4 63%); background-size: 800px 100%; animation: dashShimmer 1.3s infinite linear; }
        `}</style>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
          {Array.from({ length: 4 }).map((_, i) => <SkeletonBlock key={i} h={92} />)}
        </div>
      </div>
    );
  }
  if (error || !data) return <Empty>Error cargando las ventas del sistema sincronizado.</Empty>;

  const d = data;
  const t = d.totales || {};
  const cartera = d.cartera || { facturas_pendientes: 0, saldo_usd: 0 };
  const usarMensual = (d.ventas_diarias || []).length > 92;
  const serieTemporal = usarMensual ? d.ventas_mensuales || [] : d.ventas_diarias || [];
  const labels = serieTemporal.map((v) => (usarMensual ? v.mes : v.dia));
  const serieUSD = serieTemporal.map((v) => v.usd);
  const ma7 = serieUSD.map((_, i) => { const a = serieUSD.slice(Math.max(0, i - 6), i + 1); return a.reduce((s, x) => s + x, 0) / (a.length || 1); });
  const margenTotal = t.margen_usd ?? (d.top_productos || []).reduce((s, p) => s + (p.margen_usd || 0), 0);
  const ingresosTotal = t.total_usd || 0;
  const estados = (() => {
    const m = {};
    (d.por_estado || []).forEach((e) => {
      const label = estadoLabel(e.estado);
      if (!m[label]) m[label] = { label, value: 0, usd: 0, color: estadoColor(e.estado) };
      m[label].value += e.cantidad;
      m[label].usd += e.usd;
    });
    return Object.values(m).map((e) => ({ label: e.label, value: e.value, color: e.color, sub: money(e.usd) }));
  })();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button onClick={exportarCSV} style={S.presetBtn}>
          <FiDownload size={12} style={{ marginRight: '4px' }} /> Exportar CSV
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <div style={{ gridColumn: 'span 2' }}>
          <KPI label="Ventas totales" value={money(t.total_usd || 0)} sub={`${intFmt(t.facturas || 0)} facturas`} color={COL.green} big icon={FiDollarSign} />
        </div>
        <KPI label="Ticket promedio" value={money(t.ticket_promedio_usd || 0)} sub="por factura" color={COL.pri2} icon={FiFileText} />
        <KPI label="Cartera por cobrar" value={money(cartera.saldo_usd || 0)} sub={`${intFmt(cartera.facturas_pendientes || 0)} facturas pendientes`} color={cartera.saldo_usd > 0 ? COL.red : COL.green} icon={FiAlertTriangle} />
        <KPI label="Margen bruto" value={money(margenTotal)} sub={ingresosTotal > 0 ? `${((margenTotal / ingresosTotal) * 100).toFixed(0)}% sobre ventas totales` : '—'} color={COL.pri} icon={FiTrendingUp} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <Card title={usarMensual ? 'Ventas por mes (USD)' : 'Ventas por día (USD)'} icon={FiTrendingUp} color={COL.green}>
          <LineChart
            series={[{ name: 'Ventas USD', color: COL.green, values: serieUSD, area: true }, { name: 'Media 7', color: COL.amber, values: ma7, dash: true }]}
            labels={labels}
            fmt={(v) => '$' + (Number(v) >= 1e3 ? (v / 1e3).toFixed(1) + 'k' : Number(v).toFixed(0))}
            fmtTip={money}
            height={210}
          />
        </Card>
        <Card title="Estado de facturación" icon={FiLayers} color={COL.pri2} hint="Todas las facturas no anuladas, incluidas devoluciones.">
          <BarsH items={estados} fmt={intFmt} />
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', alignItems: 'start' }}>
        <Card title="Top productos" icon={FiPackage} color={COL.pri} style={{ marginBottom: 0 }}>
          {(d.top_productos || []).length === 0 ? (
            <Empty>Sin ventas</Empty>
          ) : (
            <>
              <table style={S.table}>
                <thead>
                  <tr style={{ background: COL.bg }}>
                    {['Producto', 'Vendido', 'Ingresos', 'Margen'].map((h) => <th key={h} style={S.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {d.top_productos.slice(0, 10).map((p, i) => (
                    <tr key={i} style={p.margen_usd < 0 ? { background: 'var(--agx-red-bg)' } : undefined}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                        <div style={{ color: COL.muted, fontSize: '10px' }}>{p.codigo}</div>
                      </td>
                      <td style={S.td}>{intFmt(p.vendido)}</td>
                      <td style={{ ...S.td, fontWeight: 600, color: COL.green }}>{money(p.ingresos_usd)}</td>
                      <td style={{ ...S.td, color: p.margen_usd > 0 ? COL.green : COL.red }}>{money(p.margen_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button onClick={onVerProductos} style={{ ...S.presetBtn, marginTop: '12px', width: '100%' }}>Ver todos los productos</button>
            </>
          )}
        </Card>
        <Card title="Top clientes" icon={FiUsers} color={COL.pri2} style={{ marginBottom: 0 }}>
          <BarsH items={(d.top_clientes || []).map((c) => ({ label: c.nombre, value: c.ingresos_usd, sub: c.rif || '' }))} fmt={money} />
          <button onClick={onVerClientes} style={{ ...S.presetBtn, marginTop: '12px', width: '100%' }}>Ver todos los clientes</button>
        </Card>
      </div>

      <div style={{ marginTop: '16px' }}>
        <button onClick={onVerVentas} style={{ ...S.presetBtn, width: '100%', padding: '10px' }}>
          <FiList size={14} style={{ marginRight: '6px' }} /> Ver todas las ventas realizadas
        </button>
      </div>
    </div>
  );
}

// ── Indicador visual de rotacion (unidades/mes en los ultimos 90 dias) ──
function RotacionBadge({ rotacion }) {
  const r = Number(rotacion) || 0;
  let color = COL.muted;
  let bg = COL.bg;
  let label = '—';
  if (r > 0) {
    label = `${r.toFixed(1)} u/mes`;
    if (r >= 30) { color = COL.green; bg = 'var(--agx-green-bg)'; }
    else if (r >= 5) { color = COL.amber; bg = 'var(--agx-blue-bg)'; }
    else { color = COL.red; bg = 'var(--agx-red-bg)'; }
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 700, color, background: bg, borderRadius: '6px', padding: '2px 7px' }}>
      {label}
    </span>
  );
}

// ── Modal de detalle de una venta (factura), reutilizable ──
function VentaDetalleModal({ numfac, onClose }) {
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    let active = true;
    setDetalle(null);
    API.get(`/ventas/erp/venta/${encodeURIComponent(numfac)}`)
      .then((r) => { if (active && r.data.status === 'success') setDetalle(r.data.data); })
      .catch(() => {});
    return () => { active = false; };
  }, [numfac]);

  if (!detalle) {
    return (
      <Modal title={`Factura #${numfac}`} onClose={onClose}>
        <SkeletonBlock h={200} />
      </Modal>
    );
  }

  const cab = detalle.cabecera || {};
  return (
    <Modal title={`Factura #${detalle.numfac}`} subtitle={`${cab.fecha || ''} · ${cab.cliente || ''}`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '16px' }}>
        <KPI label="Total" value={money(cab.total_usd)} sub={cab.estado || ''} color={COL.green} big icon={FiDollarSign} />
        <KPI label="Subtotal" value={money(cab.subtotal_usd)} sub="base" color={COL.pri2} icon={FiFileText} />
        <KPI label="IVA" value={money(cab.iva_usd)} sub="impuesto" color={COL.pri} icon={FiFileText} />
        <KPI label="Descuento" value={money(cab.descuento_usd)} sub="" color={COL.amber} icon={FiFileText} />
        <KPI label="Saldo" value={money(cab.saldo_usd)} sub="pendiente" color={cab.saldo_usd > 0 ? COL.red : COL.green} icon={FiAlertTriangle} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '16px', padding: '12px', background: COL.bg, borderRadius: '10px' }}>
        <Campo label="Cliente" value={cab.cliente} />
        <Campo label="RIF" value={cab.rif} />
        <Campo label="Dirección" value={cab.direccion} />
        <Campo label="Teléfono" value={cab.telefono} />
        <Campo label="Tipo" value={cab.tipo} />
        <Campo label="Vendedor" value={cab.vendedor} />
        <Campo label="Usuario" value={cab.usuario} />
      </div>
      <table style={S.table}>
        <thead><tr style={{ background: COL.bg }}>{['Código', 'Producto', 'Cantidad', 'Precio', 'Total', 'Margen'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
        <tbody>
          {detalle.lineas.map((l, i) => (
            <tr key={i}>
              <td style={S.td}>{l.codigo}</td>
              <td style={{ ...S.td, fontWeight: 600 }}>{l.nombre}</td>
              <td style={S.td}>{intFmt(l.cantidad)}</td>
              <td style={S.td}>{money(l.precio)}</td>
              <td style={{ ...S.td, fontWeight: 600, color: COL.green }}>{money(l.total)}</td>
              <td style={{ ...S.td, color: l.margen_usd >= 0 ? COL.green : COL.red }}>{money(l.margen_usd)}</td>
            </tr>
          ))}
          {detalle.lineas.length === 0 && <tr><td style={{ ...S.td, textAlign: 'center', color: COL.muted }} colSpan={6}>Sin líneas</td></tr>}
        </tbody>
      </table>
    </Modal>
  );
}

// ── Lista completa de ventas de un producto (scroll infinito) ──
function ProductoVentasLista({ codigo, fechaDesde, fechaHasta }) {
  const [ventas, setVentas] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [ultimoId, setUltimoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ventaDetalle, setVentaDetalle] = useState(null);
  const sentinelRef = useRef(null);

  const cargar = useCallback(async (reset) => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('limite', '50');
      if (fechaDesde) p.set('fecha_desde', fechaDesde);
      if (fechaHasta) p.set('fecha_hasta', fechaHasta);
      if (!reset && ultimoId) p.set('ultimo_id', ultimoId);
      const r = await API.get(`/ventas/erp/producto/${encodeURIComponent(codigo)}/ventas?${p.toString()}`);
      if (r.data.status === 'success') {
        const d = r.data.data;
        setVentas((prev) => (reset || !prev ? d.ventas : [...prev, ...d.ventas]));
        setHasMore(d.has_more);
        setUltimoId(d.ultimo_id);
      }
    } catch {} finally {
      setLoading(false);
    }
  }, [codigo, ultimoId, fechaDesde, fechaHasta]);

  useEffect(() => {
    cargar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo, fechaDesde, fechaHasta]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) cargar(false);
    }, { rootMargin: '200px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, cargar]);

  if (ventas === null) return <div style={{ display: 'grid', gap: '8px' }}>{Array.from({ length: 5 }).map((_, i) => <SkeletonBlock key={i} h={30} />)}</div>;

  return (
    <div>
      <table style={S.table}>
        <thead>
          <tr style={{ background: COL.bg, position: 'sticky', top: 0 }}>
            {['Fecha', 'Factura', 'Cliente', 'Cantidad', 'Precio', 'Total'].map((h) => <th key={h} style={S.th}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {ventas.map((v) => (
            <tr
              key={v.id}
              onClick={() => setVentaDetalle(v.numfac)}
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = COL.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
            >
              <td style={S.td}>{v.fecha}</td>
              <td style={{ ...S.td, fontWeight: 600, color: COL.pri2 }}>#{v.numfac}</td>
              <td style={S.td}>
                <div>{v.cliente}</div>
                {v.rif && <div style={{ color: COL.muted, fontSize: '10px' }}>{v.rif}</div>}
              </td>
              <td style={S.td}>{intFmt(v.cantidad)}</td>
              <td style={S.td}>{money(v.precio)}</td>
              <td style={{ ...S.td, fontWeight: 600, color: COL.green }}>{money(v.total)}</td>
            </tr>
          ))}
          {ventas.length === 0 && <tr><td style={{ ...S.td, textAlign: 'center', color: COL.muted }} colSpan={6}>Sin ventas</td></tr>}
        </tbody>
      </table>
      <div ref={sentinelRef} style={{ height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
        {loading && <span style={{ fontSize: '11px', color: COL.muted }}>Cargando...</span>}
        {!hasMore && ventas.length > 0 && <span style={{ fontSize: '11px', color: COL.muted }}>Fin de la lista · {ventas.length} ventas</span>}
      </div>
      {ventaDetalle && <VentaDetalleModal numfac={ventaDetalle} onClose={() => setVentaDetalle(null)} />}
    </div>
  );
}

// ── Vista Productos ──
function ProductosView({ fechaDesde, fechaHasta }) {
  const [productos, setProductos] = useState(null);
  const [q, setQ] = usePersistedState('erp.productos.q', '');
  const [sortCol, setSortCol] = usePersistedState('erp.productos.orden', 'ingresos_usd');
  const [sortDir, setSortDir] = usePersistedState('erp.productos.dir', 'desc');
  const [detalleCodigo, setDetalleCodigo] = useState(null);

  useEffect(() => {
    let active = true;
    const p = new URLSearchParams();
    if (fechaDesde) p.set('fecha_desde', fechaDesde);
    if (fechaHasta) p.set('fecha_hasta', fechaHasta);
    API.get(`/ventas/erp/productos?${p.toString()}`)
      .then((r) => { if (active && r.data.status === 'success') setProductos(r.data.data.productos || []); })
      .catch(() => active && setProductos([]));
    return () => { active = false; };
  }, [fechaDesde, fechaHasta]);

  const onSort = (col) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'nombre' || col === 'codigo' ? 'asc' : 'desc'); }
  };

  const filtrados = (productos || []).filter((p) => {
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (p.codigo || '').toLowerCase().includes(t) || (p.nombre || '').toLowerCase().includes(t);
  });
  const ordenados = sortRows(filtrados, sortCol, sortDir);
  const totalIngresos = ordenados.reduce((s, p) => s + (p.ingresos_usd || 0), 0);
  const totalVendido = ordenados.reduce((s, p) => s + (p.vendido || 0), 0);

  const columns = [
    { key: 'nombre', label: 'Producto', render: (p) => (<div><div style={{ fontWeight: 600 }}>{p.nombre}</div><div style={{ color: COL.muted, fontSize: '10px' }}>{p.codigo}</div></div>) },
    { key: 'vendido', label: 'Vendido', align: 'right', render: (p) => intFmt(p.vendido) },
    { key: 'rotacion_90d', label: 'Rotación 90d', align: 'right', render: (p) => <RotacionBadge rotacion={p.rotacion_90d} /> },
    { key: 'facturas_90d', label: 'Frec. 90d', align: 'right', render: (p) => intFmt(p.facturas_90d) },
    { key: 'ingresos_usd', label: 'Ingresos', align: 'right', render: (p) => <span style={{ fontWeight: 600, color: COL.green }}>{money(p.ingresos_usd)}</span> },
    { key: 'margen_usd', label: 'Margen', align: 'right', render: (p) => <span style={{ color: p.margen_usd >= 0 ? COL.green : COL.red }}>{money(p.margen_usd)}</span> },
    { key: 'facturas', label: 'Facturas', align: 'right', render: (p) => intFmt(p.facturas) },
    { key: 'clientes', label: 'Clientes', align: 'right', render: (p) => intFmt(p.clientes) },
  ];

  const footer = (
    <tr style={{ background: COL.bg, fontWeight: 700 }}>
      <td style={S.td}>Total ({ordenados.length} productos)</td>
      <td style={{ ...S.td, textAlign: 'right' }}>{intFmt(totalVendido)}</td>
      <td style={S.td} colSpan={2} />
      <td style={{ ...S.td, textAlign: 'right', color: COL.green }}>{money(totalIngresos)}</td>
      <td style={S.td} colSpan={3} />
    </tr>
  );

  return (
    <div>
      <SearchBox value={q} onChange={setQ} placeholder="Buscar producto por código o nombre..." countLabel={`${ordenados.length} productos · ${money(totalIngresos)}`} />

      {productos === null ? (
        <div style={{ display: 'grid', gap: '10px' }}>{Array.from({ length: 8 }).map((_, i) => <SkeletonBlock key={i} h={36} />)}</div>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ maxHeight: '62vh', overflowY: 'auto' }}>
            <DataTable columns={columns} rows={ordenados} sortCol={sortCol} sortDir={sortDir} onSort={onSort} onRowClick={(p) => setDetalleCodigo(p.codigo)} rowKey={(p) => p.codigo} emptyText="Sin resultados" footer={footer} />
          </div>
        </Card>
      )}

      {detalleCodigo && (
        <ProductoDetalleModal
          codigo={detalleCodigo}
          fechaDesde={fechaDesde}
          fechaHasta={fechaHasta}
          onClose={() => setDetalleCodigo(null)}
        />
      )}
    </div>
  );
}

// ── Modal de detalle de producto con filtro de fecha propio ──
function ProductoDetalleModal({ codigo, fechaDesde: fechaDesdeGlobal, fechaHasta: fechaHastaGlobal, onClose }) {
  const [fechaDesde, setFechaDesde] = useState(fechaDesdeGlobal);
  const [fechaHasta, setFechaHasta] = useState(fechaHastaGlobal);
  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let active = true;
    setCargando(true);
    setDetalle(null);
    const p = new URLSearchParams();
    if (fechaDesde) p.set('fecha_desde', fechaDesde);
    if (fechaHasta) p.set('fecha_hasta', fechaHasta);
    API.get(`/ventas/erp/producto/${encodeURIComponent(codigo)}?${p.toString()}`)
      .then((r) => { if (active && r.data.status === 'success') setDetalle(r.data.data); })
      .catch(() => {})
      .finally(() => active && setCargando(false));
    return () => { active = false; };
  }, [codigo, fechaDesde, fechaHasta]);

  const hayFiltro = fechaDesde || fechaHasta;

  return (
    <Modal title={detalle ? detalle.info.nombre : 'Cargando...'} subtitle={detalle ? `${detalle.codigo} · ${detalle.totales.primera_venta || '—'} → ${detalle.totales.ultima_venta || '—'}` : undefined} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', padding: '10px', background: COL.bg, borderRadius: '10px' }}>
        <FiCalendar size={14} color={hayFiltro ? COL.green : COL.muted} />
        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={S.input} />
        <span style={{ color: COL.muted, fontSize: '12px' }}>hasta</span>
        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={S.input} />
        {hayFiltro && (
          <button onClick={() => { setFechaDesde(''); setFechaHasta(''); }} style={{ ...S.presetBtn, color: COL.red }}>
            <FiX size={12} style={{ marginRight: '4px' }} /> Quitar
          </button>
        )}
      </div>

      {cargando || !detalle ? (
        <SkeletonBlock h={200} />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            <KPI label="Vendido" value={intFmt(detalle.totales.vendido)} sub="unidades" color={COL.pri2} icon={FiPackage} />
            <KPI label="Ingresos" value={money(detalle.totales.ingresos_usd)} sub={`${detalle.totales.facturas} facturas`} color={COL.green} icon={FiDollarSign} />
            <KPI label="Margen" value={money(detalle.totales.margen_usd)} sub={detalle.totales.ingresos_usd > 0 ? `${((detalle.totales.margen_usd / detalle.totales.ingresos_usd) * 100).toFixed(0)}%` : '—'} color={detalle.totales.margen_usd >= 0 ? COL.green : COL.red} icon={FiTrendingUp} />
            <KPI label="Rotación 90d" value={`${detalle.totales.rotacion_90d != null ? detalle.totales.rotacion_90d.toFixed(1) : '—'}`} sub={`${intFmt(detalle.totales.vendido_90d)} u · ${intFmt(detalle.totales.facturas_90d)} facturas`} color={detalle.totales.rotacion_90d >= 30 ? COL.green : detalle.totales.rotacion_90d >= 5 ? COL.amber : COL.red} icon={FiTrendingUp} />
            <KPI label="Rotación histórica" value={`${detalle.totales.rotacion != null ? detalle.totales.rotacion.toFixed(1) : '—'}`} sub="unidades / mes" color={COL.pri} icon={FiTrendingUp} />
            <KPI label="Clientes" value={intFmt(detalle.totales.clientes)} sub="únicos" color={COL.pri} icon={FiUsers} />
            {detalle.totales.intervalo_dias != null && (
              <KPI label="Frecuencia" value={`${detalle.totales.intervalo_dias}d`} sub="entre compras" color={COL.pri2} icon={FiShoppingCart} />
            )}
          </div>
          <ProductoGrafico ventasMensuales={detalle.ventas_mensuales} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
            <Card title="Top clientes" icon={FiUsers} color={COL.pri2}>
              <BarsH items={detalle.top_clientes.map((c) => ({ label: c.nombre, value: c.usd, sub: `${intFmt(c.vendido)} u` }))} fmt={money} />
            </Card>
          </div>
          <Card title="Ventas del producto" icon={FiShoppingCart} color={COL.pri} style={{ marginTop: '16px' }}>
            <ProductoVentasLista codigo={detalle.codigo} fechaDesde={fechaDesde} fechaHasta={fechaHasta} />
          </Card>
        </>
      )}
    </Modal>
  );
}

// ── Gráfico del producto con toggle USD / Unidades ──
function ProductoGrafico({ ventasMensuales }) {
  const [modo, setModo] = useState('usd');
  const labels = (ventasMensuales || []).map((m) => m.mes);
  const usd = (ventasMensuales || []).map((m) => m.usd);
  const unidades = (ventasMensuales || []).map((m) => m.vendido);

  return (
    <Card title="Ventas por mes" icon={FiTrendingUp} color={COL.green}>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        <button onClick={() => setModo('usd')} style={{ ...S.presetBtn, ...(modo === 'usd' ? S.presetActive : {}) }}>Ingresos (USD)</button>
        <button onClick={() => setModo('unidades')} style={{ ...S.presetBtn, ...(modo === 'unidades' ? S.presetActive : {}) }}>Cantidad (unidades)</button>
      </div>
      <LineChart
        series={[
          {
            name: modo === 'usd' ? 'USD' : 'Unidades',
            color: modo === 'usd' ? COL.green : COL.pri2,
            values: modo === 'usd' ? usd : unidades,
            area: true,
          },
        ]}
        labels={labels}
        fmt={(v) => (modo === 'usd' ? '$' + Number(v).toFixed(0) : Number(v).toFixed(0))}
        fmtTip={modo === 'usd' ? money : (v) => intFmt(v) + ' u'}
        height={200}
      />
    </Card>
  );
}

// ── Vista Clientes ──
function ClientesView({ fechaDesde, fechaHasta }) {
  const [clientes, setClientes] = useState(null);
  const [q, setQ] = usePersistedState('erp.clientes.q', '');
  const [sortCol, setSortCol] = usePersistedState('erp.clientes.orden', 'gasto_usd');
  const [sortDir, setSortDir] = usePersistedState('erp.clientes.dir', 'desc');
  const [detalle, setDetalle] = useState(null);
  const [cargandoDet, setCargandoDet] = useState(false);

  useEffect(() => {
    let active = true;
    const p = new URLSearchParams();
    if (fechaDesde) p.set('fecha_desde', fechaDesde);
    if (fechaHasta) p.set('fecha_hasta', fechaHasta);
    API.get(`/ventas/erp/clientes?${p.toString()}`)
      .then((r) => { if (active && r.data.status === 'success') setClientes(r.data.data.clientes || []); })
      .catch(() => active && setClientes([]));
    return () => { active = false; };
  }, [fechaDesde, fechaHasta]);

  const abrirDetalle = async (codigo) => {
    setCargandoDet(true);
    setDetalle(null);
    try {
      const r = await API.get(`/ventas/erp/cliente/${encodeURIComponent(codigo)}`);
      if (r.data.status === 'success') setDetalle(r.data.data);
    } catch {} finally {
      setCargandoDet(false);
    }
  };

  const onSort = (col) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir(col === 'nombre' || col === 'rif' || col === 'codigo' ? 'asc' : 'desc'); }
  };

  const filtrados = (clientes || []).filter((c) => {
    if (!q.trim()) return true;
    const t = q.trim().toLowerCase();
    return (c.codigo || '').toLowerCase().includes(t) || (c.nombre || '').toLowerCase().includes(t) || (c.rif || '').toLowerCase().includes(t);
  });
  const ordenados = sortRows(filtrados, sortCol, sortDir);
  const totalGasto = ordenados.reduce((s, c) => s + (c.gasto_usd || 0), 0);

  const columns = [
    { key: 'nombre', label: 'Cliente', render: (c) => (<div><div style={{ fontWeight: 600 }}>{c.nombre}</div><div style={{ color: COL.muted, fontSize: '10px' }}>#{c.codigo}</div></div>) },
    { key: 'rif', label: 'RIF', render: (c) => c.rif || '—' },
    { key: 'gasto_usd', label: 'Compras', align: 'right', render: (c) => <span style={{ fontWeight: 600, color: COL.green }}>{money(c.gasto_usd)}</span> },
    { key: 'facturas', label: 'Facturas', align: 'right', render: (c) => intFmt(c.facturas) },
    { key: 'saldo_usd', label: 'Saldo pendiente', align: 'right', render: (c) => <span style={{ fontWeight: 600, color: c.saldo_usd > 0 ? COL.red : COL.green }}>{c.saldo_usd > 0 ? money(c.saldo_usd) : '—'}</span> },
  ];

  const footer = (
    <tr style={{ background: COL.bg, fontWeight: 700 }}>
      <td style={S.td}>Total ({ordenados.length} clientes)</td>
      <td style={S.td} />
      <td style={{ ...S.td, textAlign: 'right', color: COL.green }}>{money(totalGasto)}</td>
      <td style={S.td} colSpan={2} />
    </tr>
  );

  return (
    <div>
      <SearchBox value={q} onChange={setQ} placeholder="Buscar cliente por nombre, RIF o código..." countLabel={`${ordenados.length} clientes · ${money(totalGasto)}`} />

      {clientes === null ? (
        <div style={{ display: 'grid', gap: '10px' }}>{Array.from({ length: 8 }).map((_, i) => <SkeletonBlock key={i} h={36} />)}</div>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ maxHeight: '62vh', overflowY: 'auto' }}>
            <DataTable columns={columns} rows={ordenados} sortCol={sortCol} sortDir={sortDir} onSort={onSort} onRowClick={(c) => abrirDetalle(c.codigo)} rowKey={(c) => c.codigo} emptyText="Sin resultados" footer={footer} />
          </div>
        </Card>
      )}

      {cargandoDet && <Modal title="Cargando..." onClose={() => setCargandoDet(false)}><SkeletonBlock h={200} /></Modal>}

      {detalle && (
        <Modal title={detalle.info.nombre} subtitle={`${detalle.info.rif || ''} · #${detalle.codigo}`} onClose={() => setDetalle(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            <KPI label="Gasto total" value={money(detalle.totales.gasto_usd)} sub={`${detalle.totales.facturas} facturas`} color={COL.green} big icon={FiDollarSign} />
            <KPI label="Saldo pendiente" value={money(detalle.totales.saldo_usd)} sub="por cobrar" color={detalle.totales.saldo_usd > 0 ? COL.red : COL.green} icon={FiAlertTriangle} />
            <KPI label="Primera compra" value={detalle.totales.primera_compra || '—'} sub="fecha" color={COL.pri2} icon={FiShoppingCart} />
            <KPI label="Última compra" value={detalle.totales.ultima_compra || '—'} sub="fecha" color={COL.pri} icon={FiShoppingCart} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '16px', padding: '12px', background: COL.bg, borderRadius: '10px' }}>
            <Campo label="Dirección" value={detalle.info.direccion} />
            <Campo label="Ciudad" value={detalle.info.ciudad} />
            <Campo label="Teléfono" value={detalle.info.telefono} />
            <Campo label="Celular" value={detalle.info.celular} />
            <Campo label="Email" value={detalle.info.email} />
            <Campo label="Representante" value={detalle.info.representante} />
            <Campo label="Límite crédito (USD)" value={detalle.info.limite_credito_usd != null ? money(detalle.info.limite_credito_usd) : null} />
            <Campo label="Días crédito" value={detalle.info.dias_credito} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <Card title="Top productos" icon={FiPackage} color={COL.pri}>
              <BarsH items={detalle.top_productos.map((p) => ({ label: p.nombre, value: p.usd, sub: `${intFmt(p.vendido)} u` }))} fmt={money} />
            </Card>
            <Card title="Últimas facturas" icon={FiFileText} color={COL.pri2}>
              <table style={S.table}>
                <thead><tr style={{ background: COL.bg }}>{['Factura', 'Fecha', 'Total', 'Saldo', 'Estado'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {detalle.ultimas_facturas.map((f, i) => (
                    <tr key={i}>
                      <td style={S.td}>#{f.numfac}</td>
                      <td style={S.td}>{f.fecha}</td>
                      <td style={{ ...S.td, fontWeight: 600 }}>{money(f.total)}</td>
                      <td style={{ ...S.td, color: f.saldo > 0 ? COL.red : COL.muted }}>{f.saldo > 0 ? money(f.saldo) : '—'}</td>
                      <td style={{ ...S.td, color: estadoColor(f.estado), fontWeight: 700 }}>{estadoLabel(f.estado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Vista Ventas (scroll infinito + orden server-side) ──
function VentasView({ fechaDesde, fechaHasta }) {
  const [ventas, setVentas] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = usePersistedState('erp.ventas.q', '');
  const [orden, setOrden] = usePersistedState('erp.ventas.orden', 'fecha');
  const [dir, setDir] = usePersistedState('erp.ventas.dir', 'desc');
  const [ventaDetalle, setVentaDetalle] = useState(null);
  const reqId = useRef(0);

  const cargar = useCallback(async (reset) => {
    const myId = ++reqId.current;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q.trim());
      if (fechaDesde) p.set('fecha_desde', fechaDesde);
      if (fechaHasta) p.set('fecha_hasta', fechaHasta);
      p.set('orden', orden);
      p.set('dir', dir);
      if (!reset && cursor) { p.set('cursor_key', String(cursor.key)); p.set('cursor_id', cursor.id); }
      const r = await API.get(`/ventas/erp/ventas?${p.toString()}`);
      if (myId !== reqId.current) return;
      if (r.data.status === 'success') {
        const d = r.data.data;
        setVentas((prev) => (reset || !prev ? d.ventas : [...prev, ...d.ventas]));
        setTotal(d.total);
        setHasMore(d.has_more);
        setCursor(d.cursor);
      }
    } catch {} finally {
      if (myId === reqId.current) setLoading(false);
    }
  }, [q, orden, dir, fechaDesde, fechaHasta, cursor]);

  useEffect(() => {
    cargar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, orden, dir, fechaDesde, fechaHasta]);

  const onSort = (col) => {
    if (orden === col) setDir(dir === 'asc' ? 'desc' : 'asc');
    else { setOrden(col); setDir(col === 'fecha' || col === 'total' || col === 'numfac' ? 'desc' : 'asc'); }
  };

  // Sentinela para scroll infinito.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) cargar(false);
    }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, cargar]);

  const columns = [
    { key: 'numfac', label: 'Factura', render: (v) => <span style={{ fontWeight: 600 }}>#{v.numfac}</span> },
    { key: 'fecha', label: 'Fecha', render: (v) => v.fecha },
    { key: 'cliente', label: 'Cliente', render: (v) => (<div><div>{v.cliente}</div>{v.rif && <div style={{ color: COL.muted, fontSize: '10px' }}>{v.rif}</div>}</div>) },
    { key: 'total_usd', label: 'Total', align: 'right', render: (v) => <span style={{ fontWeight: 600, color: COL.green }}>{money(v.total_usd)}</span> },
    { key: 'saldo_usd', label: 'Saldo', align: 'right', render: (v) => <span style={{ color: v.saldo_usd > 0 ? COL.red : COL.muted }}>{v.saldo_usd > 0 ? money(v.saldo_usd) : '—'}</span> },
    { key: 'estado', label: 'Estado', render: (v) => <span style={{ color: estadoColor(v.estado), fontWeight: 700 }}>{estadoLabel(v.estado)}</span> },
    { key: 'tipo', label: 'Tipo', render: (v) => v.tipo || '—' },
  ];

  return (
    <div>
      <SearchBox value={q} onChange={setQ} placeholder="Buscar por factura, cliente o RIF..." countLabel={`${intFmt(total)} ventas${ventas ? ` · cargadas ${ventas.length}` : ''}`} />

      {ventas === null ? (
        <div style={{ display: 'grid', gap: '10px' }}>{Array.from({ length: 10 }).map((_, i) => <SkeletonBlock key={i} h={36} />)}</div>
      ) : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ maxHeight: '62vh', overflowY: 'auto' }}>
            <DataTable columns={columns} rows={ventas} sortCol={orden} sortDir={dir} onSort={onSort} onRowClick={(v) => setVentaDetalle(v.numfac)} rowKey={(v) => v.id} emptyText="Sin resultados" />
            <div ref={sentinelRef} style={{ height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
              {loading && <span style={{ fontSize: '11px', color: COL.muted }}>Cargando...</span>}
              {!hasMore && ventas.length > 0 && <span style={{ fontSize: '11px', color: COL.muted }}>Fin de la lista</span>}
            </div>
          </div>
        </Card>
      )}

      {ventaDetalle && <VentaDetalleModal numfac={ventaDetalle} onClose={() => setVentaDetalle(null)} />}
    </div>
  );
}

// ── Componente principal con pestañas + filtro de fecha global ──
const TABS = [
  { id: 'resumen', label: 'Resumen', icon: FiBarChart2 },
  { id: 'productos', label: 'Productos', icon: FiPackage },
  { id: 'clientes', label: 'Clientes', icon: FiUsers },
  { id: 'ventas', label: 'Ventas', icon: FiList },
];

export default function DashboardERP() {
  const [tab, setTab] = usePersistedState('erp.tab', 'resumen');
  const [preset, setPreset] = usePersistedState('erp.preset', 'all');
  const [fechaDesde, setFechaDesde] = usePersistedState('erp.fechaDesde', '');
  const [fechaHasta, setFechaHasta] = usePersistedState('erp.fechaHasta', '');

  const aplicarPreset = (id) => {
    setPreset(id);
    if (id === 'custom') return;
    const [a, b] = rangoDePreset(id);
    setFechaDesde(a);
    setFechaHasta(b);
  };

  const hayFiltro = fechaDesde || fechaHasta;

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '19px', fontWeight: 800, color: COL.pri, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiBarChart2 size={20} /> Ventas del sistema sincronizado
        </div>

        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <FiCalendar size={14} color={hayFiltro ? COL.green : COL.muted} />
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => aplicarPreset(p.id)} style={{ ...S.presetBtn, ...(preset === p.id ? S.presetActive : {}) }}>
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <>
              <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={S.input} />
              <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={S.input} />
            </>
          )}
          {hayFiltro && preset !== 'custom' && (
            <button onClick={() => { setPreset('all'); setFechaDesde(''); setFechaHasta(''); }} style={{ ...S.presetBtn, color: COL.red }}>
              <FiX size={12} style={{ marginRight: '4px' }} /> Quitar
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.presetBtn, padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: '6px', ...(tab === t.id ? S.presetActive : {}) }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'resumen' && <ResumenView fechaDesde={fechaDesde} fechaHasta={fechaHasta} onVerProductos={() => setTab('productos')} onVerClientes={() => setTab('clientes')} onVerVentas={() => setTab('ventas')} />}
      {tab === 'productos' && <ProductosView fechaDesde={fechaDesde} fechaHasta={fechaHasta} />}
      {tab === 'clientes' && <ClientesView fechaDesde={fechaDesde} fechaHasta={fechaHasta} />}
      {tab === 'ventas' && <VentasView fechaDesde={fechaDesde} fechaHasta={fechaHasta} />}
    </div>
  );
}
